import { Box, Button, MenuItem, Select, TextField } from "@mui/material";
import { FC, KeyboardEvent, useState } from "react";
import { InventoryData, Status } from "../../../types/inventoryData";
import { productStatusOptions } from "../../../helpers/selectOptions";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { inventoryRules } from "../../../helpers/validationSchemes";
import { useTranslation } from "react-i18next";

const AddProductManually: FC<{
  handleAdd: (
    qrValue: string,
    status?: Status | undefined,
    note?: string | null | undefined,
    location?: string | null | undefined,
    reset?: any
  ) => void;
}> = ({ handleAdd }) => {
  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(handleAddProduct);
    }
  };

  const handleAddProduct: SubmitHandler<InventoryData> = (data) => {
    handleAdd(data.serial, data.status, data.note, data.location, reset);
  };

  const { handleSubmit, control, reset } = useForm<InventoryData>({
    defaultValues: {
      serial: "",
      status: Status.Available,
      location: "Bryggeristræde 2",
      note: "",
    },
    mode: "onChange",
  });

  const { t } = useTranslation();

  return (
    <form
      onSubmit={handleSubmit(handleAddProduct)}
      // fullWidth
      // size="medium"
    >
      <Box
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Controller
          name="serial"
          control={control}
          rules={inventoryRules.serialRule}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              id="serial"
              name="serial"
              required
              variant="filled"
              label={t("labels.serial")}
              helperText={
                <>
                  {/* Counter */}
                  {`${field?.value?.length || 0}/${
                    inventoryRules.serialRuleMaxLength
                  }`}

                  {/* Error */}
                  {error?.message &&
                    `. ${t(error.message).replace(
                      "{0}",
                      inventoryRules.serialRuleMaxLength.toString()
                    )}`}
                </>
              }
              onKeyDown={(event) => handleKeyPress(event)}
            />
          )}
        />

        <Controller
          name="status"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select {...field} id="status">
              {productStatusOptions.map((i) => (
                <MenuItem key={i.value} value={i.value}>
                  {i.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <Controller
          name="location"
          control={control}
          rules={inventoryRules.locationRule}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              id="location"
              name="location"
              variant="filled"
              label={t("inventory.location")}
              helperText={
                <>
                  {/* Counter */}
                  {`${field?.value?.length || 0}/${
                    inventoryRules.locationRuleMaxLength
                  }`}

                  {/* Error */}
                  {error?.message &&
                    `. ${t(error.message).replace(
                      "{0}",
                      inventoryRules.locationRuleMaxLength.toString()
                    )}`}
                </>
              }
              onKeyDown={(event) => handleKeyPress(event)}
            />
          )}
        />

        <Controller
          name="note"
          control={control}
          rules={inventoryRules.noteRule}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="note"
              name="note"
              variant="filled"
              label={t("inventory.note")}
              onKeyDown={(event) => handleKeyPress(event)}
              multiline
              error={!!error}
              helperText={
                <>
                  {/* Counter */}
                  {`${field?.value?.length || 0}/${
                    inventoryRules.noteRuleMaxLength
                  }`}

                  {/* Error */}
                  {error?.message &&
                    `. ${t(error.message).replace(
                      "{0}",
                      inventoryRules.noteRuleMaxLength.toString()
                    )}`}
                </>
              }
              rows={5}
            />
          )}
        />
        <Button variant="contained" type="submit" size="large">
          {t("inventory.addProduct")}
        </Button>
      </Box>
    </form>
  );
};

export default AddProductManually;
